import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { matchesService, teamsService } from '../../../services/supabaseService';
import ImportScheduleModal from './ImportScheduleModal';

const ScheduleConfigTab = ({ tournamentId }) => {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingMatch, setIsAddingMatch] = useState(false);
  const [isAutoScheduling, setIsAutoScheduling] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const [editingMatchId, setEditingMatchId] = useState(null);
  const [newMatch, setNewMatch] = useState({
    team1: '',
    team2: '',
    date: '',
    time: '',
    venue: '',
    matchType: 'group'
  });

  useEffect(() => {
    if (tournamentId && tournamentId !== 'new') {
      fetchMatchesAndTeams();
    }
  }, [tournamentId]);

  const fetchMatchesAndTeams = async () => {
    setLoading(true);
    try {
      const [matchesData, tournamentTeams] = await Promise.all([
        matchesService?.getByTournament(tournamentId),
        teamsService?.getByTournament(tournamentId)
      ]);

      const formattedMatches = matchesData?.map(match => {
        const matchDate = new Date(match?.matchDate);
        return {
          id: match?.id,
          matchNumber: match?.matchNumber,
          team1: match?.team1?.name,
          team1Id: match?.team1?.id,
          team2: match?.team2?.name,
          team2Id: match?.team2?.id,
          date: matchDate?.toISOString()?.split('T')?.[0],
          time: matchDate?.toTimeString()?.slice(0, 5),
          venue: match?.venue,
          matchType: match?.stage?.replace('_', '-'),
          status: match?.status
        };
      }) || [];

      setMatches(formattedMatches);
      setTeams(tournamentTeams?.map(tt => tt?.teams) || []);
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const teamOptions = teams?.map(team => ({
    value: team?.id,
    label: team?.name
  }));

  const venueOptions = [
    { value: 'Melbourne Cricket Ground', label: 'Melbourne Cricket Ground' },
    { value: 'Lords Cricket Ground', label: 'Lords Cricket Ground' },
    { value: 'Eden Park', label: 'Eden Park' },
    { value: 'Wankhede Stadium', label: 'Wankhede Stadium' },
    { value: 'Sydney Cricket Ground', label: 'Sydney Cricket Ground' }
  ];

  const matchTypeOptions = [
    { value: 'group', label: 'Group Stage' },
    { value: 'quarter-final', label: 'Quarter Final' },
    { value: 'semi-final', label: 'Semi Final' },
    { value: 'final', label: 'Final' }
  ];

  const handleAutoSchedule = async () => {
    if (teams?.length < 2) {
      setToastMsg({ message: 'At least 2 teams are required to generate schedule', type: 'error' });
      setTimeout(() => setToastMsg(null), 3000);
      return;
    }

    if (matches?.length > 0) {
      const confirm = window.confirm('This will replace existing matches. Continue?');
      if (!confirm) return;
    }

    setIsAutoScheduling(true);
    try {
      // Delete existing matches
      if (matches?.length > 0) {
        await Promise.all(matches?.map(m => matchesService?.delete(m?.id)));
      }

      const generatedMatches = [];
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() + 7); // Start in 7 days
      let matchNumber = 1;
      let currentDate = new Date(startDate);

      // Generate group stage matches (round-robin)
      for (let i = 0; i < teams?.length; i++) {
        for (let j = i + 1; j < teams?.length; j++) {
          const matchDateTime = new Date(currentDate);
          matchDateTime?.setHours(14, 30, 0, 0); // Default time 2:30 PM

          const matchData = {
            tournamentId,
            matchNumber,
            team1Id: teams?.[i]?.id,
            team2Id: teams?.[j]?.id,
            matchDate: matchDateTime?.toISOString(),
            venue: venueOptions?.[matchNumber % venueOptions?.length]?.value,
            stage: 'group',
            matchType: 'T20',
            status: 'upcoming'
          };

          const createdMatch = await matchesService?.create(matchData);
          
          if (createdMatch) {
            generatedMatches?.push({
              id: createdMatch?.id,
              matchNumber,
              team1: teams?.[i]?.name,
              team1Id: teams?.[i]?.id,
              team2: teams?.[j]?.name,
              team2Id: teams?.[j]?.id,
              date: matchDateTime?.toISOString()?.split('T')?.[0],
              time: '14:30',
              venue: matchData?.venue,
              matchType: 'group',
              status: 'upcoming'
            });
          }

          matchNumber++;
          // Schedule next match 2 days later
          currentDate?.setDate(currentDate?.getDate() + 2);
        }
      }

      // Generate knockout stages if enough teams
      if (teams?.length >= 4) {
        // Semi-finals
        currentDate?.setDate(currentDate?.getDate() + 3);
        for (let i = 0; i < 2; i++) {
          const matchDateTime = new Date(currentDate);
          matchDateTime?.setHours(19, 0, 0, 0); // Evening match

          const matchData = {
            tournamentId,
            matchNumber,
            team1Id: teams?.[i * 2]?.id,
            team2Id: teams?.[i * 2 + 1]?.id,
            matchDate: matchDateTime?.toISOString(),
            venue: venueOptions?.[0]?.value,
            stage: 'semi_final',
            matchType: 'T20',
            status: 'upcoming'
          };

          const createdMatch = await matchesService?.create(matchData);
          
          if (createdMatch) {
            generatedMatches?.push({
              id: createdMatch?.id,
              matchNumber,
              team1: teams?.[i * 2]?.name,
              team1Id: teams?.[i * 2]?.id,
              team2: teams?.[i * 2 + 1]?.name,
              team2Id: teams?.[i * 2 + 1]?.id,
              date: matchDateTime?.toISOString()?.split('T')?.[0],
              time: '19:00',
              venue: matchData?.venue,
              matchType: 'semi-final',
              status: 'upcoming'
            });
          }

          matchNumber++;
          currentDate?.setDate(currentDate?.getDate() + 1);
        }

        // Final
        currentDate?.setDate(currentDate?.getDate() + 2);
        const finalDateTime = new Date(currentDate);
        finalDateTime?.setHours(19, 0, 0, 0);

        const finalMatchData = {
          tournamentId,
          matchNumber,
          team1Id: teams?.[0]?.id,
          team2Id: teams?.[1]?.id,
          matchDate: finalDateTime?.toISOString(),
          venue: venueOptions?.[0]?.value,
          stage: 'final',
          matchType: 'T20',
          status: 'upcoming'
        };

        const createdFinal = await matchesService?.create(finalMatchData);
        
        if (createdFinal) {
          generatedMatches?.push({
            id: createdFinal?.id,
            matchNumber,
            team1: teams?.[0]?.name,
            team1Id: teams?.[0]?.id,
            team2: teams?.[1]?.name,
            team2Id: teams?.[1]?.id,
            date: finalDateTime?.toISOString()?.split('T')?.[0],
            time: '19:00',
            venue: finalMatchData?.venue,
            matchType: 'final',
            status: 'upcoming'
          });
        }
      }

      setMatches(generatedMatches);
    } catch (error) {
      console.error('Error auto-scheduling matches:', error);
      setToastMsg({ message: 'Failed to generate schedule. Please try again.', type: 'error' });
      setTimeout(() => setToastMsg(null), 3000);
    } finally {
      setIsAutoScheduling(false);
    }
  };

  const handleImportSchedule = async (importedMatches, importMode = 'replace') => {
    try {
      let shouldDelete = false;
      
      // Only delete existing matches if mode is 'replace'
      if (importMode === 'replace' && matches?.length > 0) {
        const confirm = window.confirm('This will replace all existing matches. Continue?');
        if (!confirm) return;
        shouldDelete = true;
      }

      // Delete existing matches if replace mode
      if (shouldDelete) {
        await Promise.all(matches?.map(m => matchesService?.delete(m?.id)));
      }

      // Get the highest match number from existing matches if adding
      let maxMatchNumber = 0;
      if (importMode === 'add') {
        const existingMatches = await matchesService?.getByTournament(tournamentId);
        maxMatchNumber = existingMatches?.length > 0 
          ? Math.max(...existingMatches?.map(m => m?.matchNumber || 0))
          : 0;
      }

      const parseDate = (dateString) => {
        if (!dateString) return null;
        
        // Try DD-MM-YYYY or DD/MM/YYYY format first
        const ddmmyyyyMatch = dateString?.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
        if (ddmmyyyyMatch) {
          const day = parseInt(ddmmyyyyMatch?.[1]);
          const month = parseInt(ddmmyyyyMatch?.[2]) - 1; // Month is 0-indexed
          const year = parseInt(ddmmyyyyMatch?.[3]);
          const date = new Date(year, month, day);
          return isNaN(date?.getTime()) ? null : date;
        }
        
        // Try MM-DD-YYYY or MM/DD/YYYY format
        const mmddyyyyMatch = dateString?.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
        if (mmddyyyyMatch) {
          const month = parseInt(mmddyyyyMatch?.[1]) - 1;
          const day = parseInt(mmddyyyyMatch?.[2]);
          const year = parseInt(mmddyyyyMatch?.[3]);
          const date = new Date(year, month, day);
          return isNaN(date?.getTime()) ? null : date;
        }
        
        // Fallback to ISO format or standard Date parsing
        const date = new Date(dateString);
        return isNaN(date?.getTime()) ? null : date;
      };

      const createdMatches = [];
      for (const match of importedMatches) {
        const parsedDate = parseDate(match?.date);
        
        if (!parsedDate || isNaN(parsedDate?.getTime())) {
          console.error(`Invalid date for match ${match?.matchNumber}: ${match?.date}`);
          console.warn(`Skipped match ${match?.matchNumber}: Invalid date format`);
          continue;
        }
        
        // Parse time (default to 14:30 if invalid)
        const timeMatch = (match?.time || '14:30')?.match(/^(\d{1,2}):(\d{2})/);
        const hours = timeMatch ? parseInt(timeMatch?.[1]) : 14;
        const minutes = timeMatch ? parseInt(timeMatch?.[2]) : 30;
        
        // Set time on the parsed date
        parsedDate?.setHours(hours, minutes, 0, 0);
        
        // Validate final datetime
        if (isNaN(parsedDate?.getTime())) {
          console.error(`Invalid datetime for match ${match?.matchNumber}`);
          console.warn(`Skipped match ${match?.matchNumber}: Invalid date/time`);
          continue;
        }
        
        // Adjust match number if adding to existing
        const adjustedMatchNumber = importMode === 'add' 
          ? maxMatchNumber + match?.matchNumber
          : match?.matchNumber;
        
        const matchData = {
          tournamentId,
          matchNumber: adjustedMatchNumber,
          team1Id: match?.team1Id,
          team2Id: match?.team2Id,
          matchDate: parsedDate?.toISOString(),
          venue: match?.venue,
          stage: match?.stage,
          matchType: 'T20',
          status: 'upcoming'
        };

        const createdMatch = await matchesService?.create(matchData);
        
        if (createdMatch) {
          const team1 = teams?.find(t => t?.id === match?.team1Id);
          const team2 = teams?.find(t => t?.id === match?.team2Id);

          createdMatches?.push({
            id: createdMatch?.id,
            matchNumber: adjustedMatchNumber,
            team1: team1?.name,
            team1Id: team1?.id,
            team2: team2?.name,
            team2Id: team2?.id,
            date: match?.date,
            time: match?.time,
            venue: match?.venue,
            matchType: match?.matchType,
            status: 'upcoming'
          });
        }
      }

      // Refresh the matches list from database to get complete updated list
      if (importMode === 'add') {
        await fetchMatchesAndTeams();
      } else {
        setMatches(createdMatches);
      }
    } catch (error) {
      console.error('Error importing schedule:', error);
      setToastMsg({ message: 'Failed to import schedule. Please try again.', type: 'error' });
      setTimeout(() => setToastMsg(null), 3000);
    }
  };

  const getMatchTypeBadge = (type) => {
    const badgeMap = {
      'group': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'quarter-final': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'semi-final': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      'final': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return badgeMap?.[type] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleAddMatch = async () => {
    if (!newMatch?.team1 || !newMatch?.team2 || !newMatch?.date || !newMatch?.time || !newMatch?.venue) {
      return;
    }

    try {
      const matchDateTime = new Date(`${newMatch?.date}T${newMatch?.time}:00`);
      
      // Fetch the highest match_number for this tournament from database
      const existingMatches = await matchesService?.getByTournament(tournamentId);
      let maxMatchNumber = existingMatches?.length > 0 
        ? Math.max(...existingMatches?.map(m => m?.matchNumber || 0))
        : 0;
      
      const matchData = {
        tournamentId,
        matchNumber: maxMatchNumber + 1,
        team1Id: newMatch?.team1,
        team2Id: newMatch?.team2,
        matchDate: matchDateTime?.toISOString(),
        venue: newMatch?.venue,
        stage: newMatch?.matchType?.replace('-', '_'),
        matchType: 'T20',
        status: 'upcoming'
      };

      const createdMatch = await matchesService?.create(matchData);

      if (createdMatch) {
        const team1 = teams?.find(t => t?.id === newMatch?.team1);
        const team2 = teams?.find(t => t?.id === newMatch?.team2);

        const match = {
          id: createdMatch?.id,
          matchNumber: createdMatch?.matchNumber,
          team1: team1?.name,
          team1Id: team1?.id,
          team2: team2?.name,
          team2Id: team2?.id,
          date: newMatch?.date,
          time: newMatch?.time,
          venue: newMatch?.venue,
          matchType: newMatch?.matchType,
          status: 'upcoming'
        };

        setMatches([...matches, match]);
        setNewMatch({
          team1: '',
          team2: '',
          date: '',
          time: '',
          venue: '',
          matchType: 'group'
        });
        setIsAddingMatch(false);
      }
    } catch (error) {
      console.error('Error adding match:', error);
    }
  };

  const handleDeleteMatch = async (matchId) => {
    try {
      const success = await matchesService?.delete(matchId);
      
      if (success) {
        setMatches(matches?.filter(m => m?.id !== matchId));
      }
    } catch (error) {
      console.error('Error deleting match:', error);
    }
  };

  const handleEditMatch = (match) => {
    setEditingMatchId(match?.id);
    setNewMatch({
      team1: match?.team1Id,
      team2: match?.team2Id,
      date: match?.date,
      time: match?.time,
      venue: match?.venue,
      matchType: match?.matchType
    });
    setIsAddingMatch(true);
  };

  const handleUpdateMatch = async () => {
    if (!newMatch?.team1 || !newMatch?.team2 || !newMatch?.date || !newMatch?.time || !newMatch?.venue) {
      return;
    }

    try {
      const matchDateTime = new Date(`${newMatch?.date}T${newMatch?.time}:00`);
      
      const matchData = {
        team1Id: newMatch?.team1,
        team2Id: newMatch?.team2,
        matchDate: matchDateTime?.toISOString(),
        venue: newMatch?.venue,
        stage: newMatch?.matchType?.replace('-', '_'),
        matchType: 'T20',
        status: 'upcoming'
      };

      const updatedMatch = await matchesService?.update(editingMatchId, matchData);

      if (updatedMatch) {
        const team1 = teams?.find(t => t?.id === newMatch?.team1);
        const team2 = teams?.find(t => t?.id === newMatch?.team2);

        const updatedMatchData = {
          id: editingMatchId,
          matchNumber: matches?.find(m => m?.id === editingMatchId)?.matchNumber,
          team1: team1?.name,
          team1Id: team1?.id,
          team2: team2?.name,
          team2Id: team2?.id,
          date: newMatch?.date,
          time: newMatch?.time,
          venue: newMatch?.venue,
          matchType: newMatch?.matchType,
          status: 'upcoming'
        };

        setMatches(matches?.map(m => m?.id === editingMatchId ? updatedMatchData : m));
        setNewMatch({
          team1: '',
          team2: '',
          date: '',
          time: '',
          venue: '',
          matchType: 'group'
        });
        setIsAddingMatch(false);
        setEditingMatchId(null);
      }
    } catch (error) {
      console.error('Error updating match:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground">Match Schedule</h3>
          <p className="text-sm text-muted-foreground mt-1">Configure tournament fixtures and venues</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            iconName="Upload"
            iconPosition="left"
            className="w-full sm:w-auto"
            onClick={() => setIsImportModalOpen(true)}
            disabled={teams?.length < 2}
          >
            Import CSV
          </Button>
          <Button
            variant="outline"
            iconName="Calendar"
            iconPosition="left"
            className="w-full sm:w-auto"
            onClick={handleAutoSchedule}
            disabled={isAutoScheduling || teams?.length < 2}
          >
            {isAutoScheduling ? 'Generating...' : 'Auto Schedule'}
          </Button>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setIsAddingMatch(true)}
            className="w-full sm:w-auto"
          >
            Add Match
          </Button>
        </div>
      </div>
      {isAddingMatch && (
        <div className="bg-card border border-border rounded-lg p-4 md:p-6 space-y-4">
          <h4 className="text-base font-semibold text-foreground mb-4">{editingMatchId ? 'Edit Match' : 'Add New Match'}</h4>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Select
              label="Team 1"
              placeholder="Select team"
              options={teamOptions}
              value={newMatch?.team1}
              onChange={(value) => setNewMatch({ ...newMatch, team1: value })}
              required
            />
            <Select
              label="Team 2"
              placeholder="Select team"
              options={teamOptions}
              value={newMatch?.team2}
              onChange={(value) => setNewMatch({ ...newMatch, team2: value })}
              required
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Input
              label="Match Date"
              type="date"
              value={newMatch?.date}
              onChange={(e) => setNewMatch({ ...newMatch, date: e?.target?.value })}
              required
            />
            <Input
              label="Match Time"
              type="time"
              value={newMatch?.time}
              onChange={(e) => setNewMatch({ ...newMatch, time: e?.target?.value })}
              required
            />
            <Select
              label="Match Type"
              options={matchTypeOptions}
              value={newMatch?.matchType}
              onChange={(value) => setNewMatch({ ...newMatch, matchType: value })}
            />
          </div>

          <Select
            label="Venue"
            placeholder="Select venue"
            options={venueOptions}
            value={newMatch?.venue}
            onChange={(value) => setNewMatch({ ...newMatch, venue: value })}
            required
          />

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              variant="default"
              onClick={editingMatchId ? handleUpdateMatch : handleAddMatch}
              iconName="Check"
              iconPosition="left"
              className="flex-1 sm:flex-initial"
            >
              {editingMatchId ? 'Update Match' : 'Add Match'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddingMatch(false);
                setEditingMatchId(null);
                setNewMatch({
                  team1: '',
                  team2: '',
                  date: '',
                  time: '',
                  venue: '',
                  matchType: 'group'
                });
              }}
              iconName="X"
              iconPosition="left"
              className="flex-1 sm:flex-initial"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {matches?.map((match) => (
          <div
            key={match?.id}
            className="bg-card border border-border rounded-lg p-4 md:p-6 hover:shadow-elevation-sm transition-shadow"
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm font-semibold text-muted-foreground">Match {match?.matchNumber}</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getMatchTypeBadge(match?.matchType)}`}>
                    {match?.matchType?.replace('-', ' ')}
                  </span>
                </div>
                
                <div className="flex items-center gap-4 mb-3">
                  <span className="text-base md:text-lg font-semibold text-foreground">{match?.team1}</span>
                  <span className="text-muted-foreground">vs</span>
                  <span className="text-base md:text-lg font-semibold text-foreground">{match?.team2}</span>
                </div>

                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name="Calendar" size={16} />
                    <span>{formatDate(match?.date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={16} />
                    <span>{formatTime(match?.time)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={16} />
                    <span className="truncate max-w-[200px]">{match?.venue}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  iconName="Edit"
                  onClick={() => handleEditMatch(match)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  iconName="Trash2"
                  onClick={() => handleDeleteMatch(match?.id)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      {matches?.length === 0 && (
        <div className="text-center py-12 bg-muted rounded-lg">
          <Icon name="Calendar" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No matches scheduled yet</p>
          <Button
            variant="default"
            onClick={() => setIsAddingMatch(true)}
            iconName="Plus"
            iconPosition="left"
            className="mt-4"
          >
            Add First Match
          </Button>
        </div>
      )}

      <ImportScheduleModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImportSchedule}
        teams={teams}
      />
      {toastMsg && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${toastMsg?.type === 'error' ? 'bg-destructive text-destructive-foreground' : 'bg-success text-success-foreground'}`}>
          {toastMsg?.message}
        </div>
      )}
    </div>
  );
};

export default ScheduleConfigTab;